import dayjs from 'dayjs';
import { MapUtils } from '../../utility/map-utils.js';
import { Country } from './country.js';
import { getLanguageOfCountry, Language } from './language.js';
import { MembershipRole } from './membership-role.js';
import { VersionRange } from '../core/version-range.js';
import { DataModelBuilder, isSameList, SerializationContext, SerializedData } from '../dto/dto.js';
import { BusinessModuleRef } from './business-module-ref.js';
import { BusinessModuleType } from './business-module-type.js';
import { SchedulingMode } from './scheduling-mode.js';
import { TimeZoneRegion } from './time-zone-region.js';
import { fromEnumAsInt, fromEnumAsString, toEnumFromInt, toEnumFromString } from '../../serialization/enum-utils.js';
import { DashboardType, getDashboardFromBusinessModule } from './dashboard-type.js';
import { BusinessMembershipBase } from '../../contracts/business-membership.js';

export class BusinessMembership extends BusinessMembershipBase {
  static readonly TYPE_ID: string = 'f44f3c74e9514cfea8c4dc03f501dea6';
  static readonly SCHEMA_VERSION: number = 13;

  readonly businessRef: BusinessModuleRef;

  private constructor(
        public readonly uniqueId: string,
        public readonly businessId: string,
        public readonly creatorId: string,
        public readonly organizationId: string,
        public readonly name: string,
        public readonly partnerRole: MembershipRole,
        public readonly creationDate: dayjs.Dayjs,
        public readonly country: Country,
        public readonly language: Language,
        public readonly memberId: string,
        public readonly timezone: TimeZoneRegion,
        public readonly moduleType: BusinessModuleType,
        private readonly dashboards: DashboardType[],
  ) {
    super();
    this.businessRef = BusinessModuleRef.create(uniqueId, organizationId, moduleType);
  }

  get $typeId(): string {
    return BusinessMembership.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.exactly(BusinessMembership.SCHEMA_VERSION);
  }

  hasDashboard(dashboard: DashboardType): boolean {
    return this.dashboards.includes(dashboard);
  }

  addDashboard(dashboard: DashboardType): void {
    this.dashboards.unshift(dashboard);
  }

  getDashboards(): DashboardType[] {
    return [...this.dashboards];
  }

  static createLegacy(args: {
    uniqueId: string,
    businessId: string,
    creatorId: string,
    organizationId: string,
    name: string,
    partnerRole: MembershipRole,
    creationDate: dayjs.Dayjs,
    country: Country,
    language: Language,
    memberId: string,
    timezone: TimeZoneRegion,
    moduleType: BusinessModuleType,
    dashboard: DashboardType,
  }): BusinessMembership {
    return new BusinessMembership(
        args.uniqueId,
        args.businessId,
        args.creatorId,
        args.organizationId,
        args.name,
        args.partnerRole,
        args.creationDate,
        args.country,
        args.language,
        args.memberId,
        args.timezone,
        args.moduleType,
        [args.dashboard]
    );
  }

  static create(args: {
    uniqueId: string,
    businessId: string,
    creatorId: string,
    organizationId: string,
    name: string,
    partnerRole: MembershipRole,
    creationDate: dayjs.Dayjs,
    country: Country,
    language: Language,
    memberId: string,
    timezone: TimeZoneRegion,
    moduleType: BusinessModuleType,
    dashboards: DashboardType[] | null,
  }): BusinessMembership {
    return new BusinessMembership(
        args.uniqueId,
        args.businessId,
        args.creatorId,
        args.organizationId,
        args.name,
        args.partnerRole,
        args.creationDate,
        args.country,
        args.language,
        args.memberId,
        args.timezone,
        args.moduleType,
        args.dashboards ?? [],
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): BusinessMembership {
    return new BusinessMembership(
        MapUtils.get<string>(map, 'uniqueId'),
        MapUtils.get<string>(map, 'businessId'),
        MapUtils.get<string>(map, 'creatorId'),
        MapUtils.get<string>(map, 'organizationId'),
        MapUtils.get<string>(map, 'name'),
        toEnumFromInt(MapUtils.get<number>(map, 'partnerRole'), MembershipRole),
        context.decodeDate(MapUtils.get<number>(map, 'creationDate')),
        toEnumFromString(MapUtils.get<string>(map, 'country'), Country),
        toEnumFromString(MapUtils.get<string>(map, 'language'), Language),
        MapUtils.get<string>(map, 'memberId'),
        toEnumFromString(MapUtils.get<string>(map, 'timezone'), TimeZoneRegion),
        toEnumFromString(MapUtils.get<string>(map, 'moduleType'), BusinessModuleType),
        MapUtils.getList<DashboardType>(map, 'dashboards', (data) => toEnumFromString(data as string, DashboardType)),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'businessId': this.businessId,
      'creatorId': this.creatorId,
      'organizationId': this.organizationId,
      'moduleType': fromEnumAsString(this.moduleType),
      'memberId': this.memberId,
      'name': this.name,
      'partnerRole': fromEnumAsInt(this.partnerRole),
      'creationDate': context.encodeDate(this.creationDate),
      'country': fromEnumAsString(this.country),
      'language': fromEnumAsString(this.language),
      'timezone': fromEnumAsString(this.timezone),
      'dashboards': this.dashboards.map((e) => fromEnumAsString(e)),
    };
  }

  clone(): this {
    return new BusinessMembership(
        this.uniqueId,
        this.businessId,
        this.creatorId,
        this.organizationId,
        this.name,
        this.partnerRole,
        this.creationDate,
        this.country,
        this.language,
        this.memberId,
        this.timezone,
        this.moduleType,
        [...this.dashboards],
    ) as this;
  }

  isSame(other: BusinessMembership): boolean {
    if(this === other) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
          other.businessRef === this.businessRef &&
          other.creatorId === this.creatorId &&
          other.organizationId === this.organizationId &&
          other.name === this.name &&
          other.partnerRole === this.partnerRole &&
          other.creationDate === this.creationDate &&
          other.country === this.country &&
          other.language === this.language &&
          other.memberId === this.memberId &&
          other.timezone === this.timezone &&
          other.moduleType == this.moduleType &&
          isSameList(other.dashboards, this.dashboards);
  }

  copyWith(fields?: {
    businessId?: string,
    organizationId?: string,
    moduleType?: BusinessModuleType,
  }): this {
    return new BusinessMembership(
        this.uniqueId,
        fields?.businessId ?? this.businessId,
        this.creatorId,
        fields?.organizationId ?? this.organizationId,
        this.name,
        this.partnerRole,
        this.creationDate,
        this.country,
        this.language,
        this.memberId,
        this.timezone,
        fields?.moduleType ?? this.moduleType,
        [...this.dashboards],
    ) as this;
  }
}

export class BusinessMembershipBuilder extends DataModelBuilder<BusinessMembership> {
  build(context: SerializationContext, map: SerializedData): BusinessMembership {
    return BusinessMembership.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);
    if(versionRange.contains(BusinessMembership.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(BusinessMembership.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    if(version < 2) {
      MapUtils.set(map, 'userRole', MapUtils.get(map, 'role'));
      version = 2;
    }

    if(version < 3) {
      MapUtils.set(map, 'schedulingMode', SchedulingMode.Calendar);
      version = 3;
    }

    // In this version, migration safety was introduced for [schedulingMode].
    if(version < 4) {
      version = 4;
    }

    // In this version the [schedulingMode] field was removed
    if(version < 5) {
      version = 5;
    }

    // / In this version the [language] field was removed
    if(version < 6) {
      const country = MapUtils.get<Country>(map, 'country');
      MapUtils.setIfNullOrUndefined(map, 'language', getLanguageOfCountry(country));
      version = 6;
    }

    // / In this version the [isDemoBusiness] field was removed
    if(version < 7) {
      version = 7;
    }

    // / In this version the [businessId] field was introduced
    if(version < 8) {
      MapUtils.set(map, 'businessId', MapUtils.get(map, 'uniqueId'));
      version = 8;
    }

    // / In this version the `organizationId` field was added
    if(version < 9) {
      MapUtils.setIfNullOrUndefined(map, 'organizationId', MapUtils.get(map, 'creatorId'));
      version = 9;
    }

    // / In this version the `bookingType` field was added
    if(version < 10) {
      MapUtils.set(map, 'moduleType', BusinessModuleType.Legacy);
      version = 10;
    }

    // In this version the `userRole` field was replaced with `partnerRole`.
    if(version < 11) {
      version = 11;
      map['partnerRole'] = map['userRole'];
    }

    // In this version the `dashboards` field was added.
    if(version < 12) {
      version = 12;

      const moduleType: BusinessModuleType = toEnumFromString(MapUtils.get<string>(map, 'moduleType'), BusinessModuleType);
      const dashboardType: DashboardType = getDashboardFromBusinessModule(moduleType);

      map['dashboards'] = [fromEnumAsString(dashboardType)];
    }

    // In this version the `userRole` field was replaced with `partnerRole`.
    if(version < 13) {
      version = 13;
      map['memberId'] = map['staffId'];
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }

  migrateFromCurrentSchemaVersion(
      context: SerializationContext,
      model: BusinessMembership,
      map: SerializedData
  ): SerializedData {
    const versionRange = MapUtils.getVersionRange(map);
    let version = versionRange.from;

    if(version >= 11) {
      version = 8;
      map['userRole'] = map['partnerRole'];
      map['staffId'] = map['memberId'];
      map['$schema_version'] = VersionRange.encodedBetween(8, 13);
    }

    return map;
  }
}
